import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger, useGSAP);

type Mode = 'mask' | 'scrub' | 'fade';

interface WordRevealProps {
  text: string;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
  /**
   * mask  — words slide up out of an overflow mask when scrolled into view
   * scrub — words brighten one-by-one, tied to scroll position (quote style)
   * fade  — words fade/rise once on enter
   */
  mode?: Mode;
  stagger?: number;
  delay?: number;
  /** scrub mode: dim color the words start at */
  dimClass?: string;
}

const WordReveal: React.FC<WordRevealProps> = ({
  text,
  as: Tag = 'span',
  className,
  mode = 'mask',
  stagger = 0.04,
  delay = 0,
  dimClass,
}) => {
  const ref = useRef<HTMLElement>(null);
  const words = text.split(' ');

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      const targets = el.querySelectorAll<HTMLElement>('.wr-word');
      if (!targets.length) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        gsap.set(targets, { yPercent: 0, opacity: 1 });
        return;
      }

      if (mode === 'mask') {
        gsap.fromTo(
          targets,
          { yPercent: 110 },
          {
            yPercent: 0,
            duration: 0.9,
            ease: 'power4.out',
            stagger,
            delay,
            scrollTrigger: { trigger: el, start: 'top 88%', once: true },
          }
        );
      } else if (mode === 'scrub') {
        gsap.fromTo(
          targets,
          { opacity: 0.16 },
          {
            opacity: 1,
            ease: 'none',
            stagger: 0.06,
            scrollTrigger: {
              trigger: el,
              start: 'top 80%',
              end: 'bottom 45%',
              scrub: 0.4,
            },
          }
        );
      } else {
        gsap.fromTo(
          targets,
          { opacity: 0, y: 14 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power3.out',
            stagger,
            delay,
            scrollTrigger: { trigger: el, start: 'top 90%', once: true },
          }
        );
      }
    },
    { scope: ref, dependencies: [mode] }
  );

  const Comp = Tag as React.ElementType;

  return (
    <Comp ref={ref} className={`wr ${className ?? ''}`}>
      {words.map((word, i) =>
        mode === 'mask' ? (
          <span key={i} className='wr-mask'>
            <span className='wr-word'>{word}</span>
            {i < words.length - 1 ? ' ' : ''}
          </span>
        ) : (
          <span key={i} className={`wr-word ${dimClass ?? ''}`}>
            {word}
            {i < words.length - 1 ? ' ' : ''}
          </span>
        )
      )}
    </Comp>
  );
};

export default WordReveal;
